import { isPlatformBrowser } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  ElementRef,
  EventEmitter,
  Inject,
  Input,
  OnInit,
  Output,
  PLATFORM_ID,
  signal,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DeviceService } from '@shared/services';

const DEFAULT_HEIGHT = 300;
const MINIMAL_HEIGHT_DIFFERENCE = 100;

@Component({
  selector: 'sd-spoiler',
  standalone: true,
  templateUrl: './spoiler.component.html',
  styleUrls: ['./spoiler.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SdvorSpoilerComponent implements OnInit, AfterViewInit {
  @ViewChild('contentElement') contentElement: ElementRef;

  @Input() height: number = DEFAULT_HEIGHT;
  @Input() showAllText?: string;
  @Input() collapseText?: string;
  @Input() dataAnalytics?: string;

  // eslint-disable-next-line @angular-eslint/no-output-native
  @Output() toggle = new EventEmitter<boolean>();

  @Input() set opened(opened: boolean) {
    this.isOpened.set(opened);
  }

  @Input() set disabledOpen(disabledOpen: boolean) {
    this.isDisabledOpen.set(disabledOpen);
  }

  isOpened = signal(false);
  isDisabledOpen = signal(false);
  scrollHeight = signal(0);

  heightInPx = computed(() => {
    const isOpened = this.isOpened();
    const contentHeight = this.scrollHeight();
    const showFull = contentHeight - this.height > MINIMAL_HEIGHT_DIFFERENCE;

    if (isOpened || !showFull) {
      return `${contentHeight}px`;
    }

    return `${this.height}px`;
  });

  displayEnabled = computed(() => {
    return this.scrollHeight() - this.height > MINIMAL_HEIGHT_DIFFERENCE;
  });

  constructor(
    private destroyRef: DestroyRef,
    private deviceService: DeviceService,
    @Inject(PLATFORM_ID) private platformId: string,
  ) {}

  ngOnInit(): void {
    this.deviceService.isTabletMdUp$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((isTabletMdUp) => {
      if (!isTabletMdUp) {
        this.isOpened.set(false);
      }
    });
  }

  ngAfterViewInit(): void {
    if (isPlatformBrowser(this.platformId)) {
      const native = this.contentElement.nativeElement;

      const resizeObs = new ResizeObserver(() => {
        const height = native.scrollHeight;
        if (height > 0) {
          this.scrollHeight.set(height);
        }
      });

      resizeObs.observe(native);

      this.scrollHeight.set(native.scrollHeight);
    }
  }

  onToggle(): void {
    const newState = !this.isOpened();
    if (!this.isDisabledOpen()) {
      this.isOpened.set(newState);
    }
    this.toggle.emit(newState);
  }
}
